import { useLatest } from '@qfa/react-components';
import { motion } from 'framer-motion';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { AnimationDirection } from '../types';
import { ContractCSSKey, ContractProps } from './types';

/**
 * This component is used for things like a sidebar that you want to show/hide by expanding/contracting. Animates the element to/from 0 width using negative margin.
 *
 * Content doesn't need static size.
 */
const Contract: React.FunctionComponent<ContractProps> = (props) => {
  const { isMounted, children, direction, duration = 0.25, className } = props;

  const contentRef = useRef<HTMLDivElement>(null);

  const [width, setWidth] = useState(0);
  const latestWidth = useLatest(width);
  const [height, setHeight] = useState(0);
  const latestHeight = useLatest(height);

  const [isFirstRender, setIsFirstRender] = useState(true);
  const [initialStateFixed, setInitialStateFixed] = useState(false);

  const setSize = useCallback((rect: DOMRectReadOnly) => {
    setWidth(rect.width);
    setHeight(rect.height);
  }, []);

  const observer = useMemo(() => {
    const callback: ResizeObserverCallback = (entries) => {
      // should only be 1 entry
      for (const entry of entries) {
        setSize(entry.contentRect);
      }
    };

    return new ResizeObserver(callback);
  }, [setSize]);

  useEffect(() => {
    const el = contentRef.current;
    if (el) {
      observer.observe(el);
    }
    return () => observer.disconnect();
  }, [observer]);

  // only execute on first render
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (contentRef.current && isFirstRender) {
      latestWidth.current = contentRef.current.clientWidth;
      latestHeight.current = contentRef.current.clientHeight;
      setWidth(contentRef.current.clientWidth);
      setHeight(contentRef.current.clientHeight);
      setIsFirstRender(false);
    }
  });

  useEffect(() => {
    if (!isFirstRender) {
      setInitialStateFixed(true);
    }
  }, [isFirstRender]);

  const getCssKey = (direction: AnimationDirection) => {
    let cssKey: ContractCSSKey;
    switch (direction) {
      case AnimationDirection.TO_LEFT:
        cssKey = 'marginRight';
        break;
      case AnimationDirection.TO_RIGHT:
        cssKey = 'marginLeft';
        break;
      case AnimationDirection.TO_TOP:
        cssKey = 'marginBottom';
        break;
      case AnimationDirection.TO_BOTTOM:
        cssKey = 'marginTop';
        break;
    }
    return cssKey;
  };

  const variants = {
    hidden() {
      const isHorizontal =
        direction === AnimationDirection.TO_LEFT || direction === AnimationDirection.TO_RIGHT;
      const cssKey = getCssKey(direction);
      return {
        [cssKey]: -((isHorizontal ? latestWidth.current : latestHeight.current) || 0),
      };
    },
    shown() {
      const cssKey = getCssKey(direction);
      return {
        [cssKey]: 0,
      };
    },
  };

  const renderContract = () => {
    const transitionDuration = !initialStateFixed ? 0 : duration;
    const animate = isFirstRender ? 'shown' : isMounted ? 'shown' : 'hidden';

    return (
      <motion.div
        variants={variants}
        initial={false}
        animate={animate}
        transition={{ duration: transitionDuration }}
        ref={contentRef}
        className={className}
      >
        {children}
      </motion.div>
    );
  };

  return renderContract();
};

export default React.memo(Contract);
